import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { Logger } from '@puzzlemania/shared';
import { composeServices, composeServicesSync, type ComposedServices } from './composeServices';
import { RootErrorFallback } from '../observability/RootErrorFallback';
import type { PlatformServices } from './createPlatformServices';

export type { ComposedServices };

// The default value doubles as the fallback for components rendered without a
// PlatformServicesProvider ancestor (existing unit tests render screens in isolation
// and inject their own fakes via props) — it is built once, here, so no screen ever
// constructs a RemoteConfigService/EconomyService itself.
const PlatformServicesContext = createContext<ComposedServices>(composeServicesSync());

export function PlatformServicesProvider({
  children,
  overrides,
  logger,
}: {
  children: ReactNode;
  overrides?: Partial<PlatformServices>;
  /** The same instance main.tsx constructed for the root ErrorBoundary/global handlers. Defaults to a fresh one (test-only fallback). */
  logger?: Logger;
}) {
  const [services, setServices] = useState<ComposedServices | null>(null);
  /**
   * 2026-08-14 — composition REJECTED, so there is nothing to render and never
   * will be without a reload.
   *
   * Tracked separately from `services` because the two "no services" states are
   * different and the difference is everything the player sees: `null` while it
   * is still resolving is a legitimate blank frame for a few hundred ms, and
   * `null` after a rejection is a blank page forever. Until this existed they
   * were the same state — the promise was `void`-ed with no `.catch`, so a
   * rejection left `services` at `null` silently and `return null` below made
   * the app render nothing, permanently. No error boundary caught it, because a
   * rejected promise is not a render throw.
   *
   * Measured by failing exactly one of the three save reads with a 401:
   * `visibleText: ""`, indefinitely. See `SaveService.loadAll`, which is now
   * hardened so this path needs a much rarer fault to reach — but "much rarer"
   * is not "impossible", and a blank page is the worst possible way to find out.
   */
  const [failed, setFailed] = useState(false);
  const [loggerInstance] = useState(() => logger ?? new Logger(import.meta.env.DEV));

  useEffect(() => {
    let disposed = false;
    let disposeFn: (() => void) | null = null;

    void composeServices(overrides, loggerInstance)
      .then(({ services: composed, dispose }) => {
        // React Strict Mode double-invokes this effect (mount, cleanup, mount); if the
        // first invocation's cleanup already ran by the time this resolves, discard its
        // subscription instead of leaking a second wallet-persistence listener.
        if (disposed) {
          dispose();
          return;
        }
        disposeFn = dispose;
        setServices(composed);
      })
      .catch((error: unknown) => {
        // The one thing that must not happen here is nothing.
        if (disposed) return;
        loggerInstance.error('compose_services_failed', error);
        setFailed(true);
      });

    return () => {
      disposed = true;
      disposeFn?.();
    };
    // overrides is only ever provided by tests with a stable reference for the
    // lifetime of the Provider — composing again on change would violate the
    // "one instance per service per app load" rule.
  }, []);

  // A boot that failed says so, and offers the reload that fixes it. Checked
  // before `!services`, since a failed compose leaves that null too.
  if (failed) return <RootErrorFallback />;
  if (!services) return null;

  return (
    <PlatformServicesContext.Provider value={services}>{children}</PlatformServicesContext.Provider>
  );
}

export function usePlatformServices(): ComposedServices {
  return useContext(PlatformServicesContext);
}
